import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { MemberAvatar } from '@/components/MemberAvatar';
import { cn } from '@/lib/utils';

/* ─────────────────────────────────────────────────────────────────────────
 * Glass redesign — Phase 6 Calendar.
 *
 * Direct port of `app/fluxter/project/{calendar.css, calendar.jsx}`.
 * Three views:
 *   - Month — 6×7 grid, density-aware chip overflow
 *   - Week  — all-day strip + hour grid (7am–7pm), now-line
 *   - Day   — 2-pane (agenda + side rail w/ MiniMonth + Tasks-due)
 *
 * Data-source agnostic: takes `tasks: CalendarTaskRow[]` so both the
 * per-project route and the workspace-home widget share one component.
 *
 * Drag-to-reschedule: when an `onReschedule` callback is supplied, every
 * task chip in Month / Week all-day / Overdue lane becomes draggable,
 * and Month day cells + Week all-day buckets become drop targets. Drop
 * fires `onReschedule(taskUlid, "YYYY-MM-DD")`; the callback is
 * expected to wire `useRescheduleTask` on the page side. Day view's
 * single-cell layout has no useful drop target so chips there stay
 * click-only. Hour-grid cells in Week / Day are reserved for the
 * future timed-events stream and aren't drop targets.
 *
 * Per Phase 6 Q2: no timed events (CalendarEvent model deferred). The
 * shell is built so a future timed-events stream slots in via a
 * second `events: TimedEvent[]` prop without restructuring.
 * Per Q3: Tasks/Meetings filter chips render faithfully — Meetings
 * filters to nothing today, auto-works once timed events land.
 * Per Q5: day-view stats show task-due count instead of meeting hours.
 * Per Q6: tweaks (density / firstDay / showWeekends) hardcoded;
 * per-user preferences API ships in a follow-up slice.
 * ───────────────────────────────────────────────────────────────────── */

export interface CalendarTaskRow {
  id: string;
  code: string;
  title: string;
  statusColor: string;
  isComplete: boolean;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  /** ISO 8601 datetime — the task's due_at. */
  dueAt: string;
}

/**
 * Slice 13b — Calendar event surface.
 *
 * Adapter shape consumed by `CalendarView`. Pages map their
 * `useCalendarEvents()` payload into this list so the calendar
 * component itself stays data-source agnostic (matches how
 * `CalendarTaskRow` is mapped from the tasks query).
 */
export interface CalendarEventRow {
  id: string;
  title: string;
  /** ISO 8601 — full timestamp for timed, calendar-date for all-day. */
  startAt: string;
  /** Null for single-day all-day events; full ISO for everything else. */
  endAt: string | null;
  isAllDay: boolean;
  location: string | null;
  projectName: string | null;
  attendees: ReadonlyArray<{ userId: string; name: string | null; email: string | null }>;
}

export interface CalendarMember {
  id: string;
  name: string | null;
  email: string | null;
}

export type CalendarViewMode = 'day' | 'week' | 'month';

export interface CalendarViewProps {
  tasks: CalendarTaskRow[];
  members: CalendarMember[];
  /**
   * Calendar events (meetings, blocks, deadlines). Optional so older
   * surfaces can keep rendering tasks-only while we wire 13b across
   * the SPA. Empty default — same shape as the audit's "events: []"
   * placeholder Phase 6 reserved for this slice.
   */
  events?: CalendarEventRow[];
  /**
   * Controlled view mode. Lifted from internal state in 13c so the
   * page can derive the visible date range and feed it into
   * `useCalendarEvents` for narrow-window refetches as the user
   * navigates. Defaults to `'week'` if the page-side state initialises
   * there.
   */
  view: CalendarViewMode;
  setView: (next: CalendarViewMode) => void;
  /** Controlled focal date (the date the current view is centered on). */
  focal: Date;
  setFocal: (next: Date) => void;
  /** Open the task detail page. */
  onOpenTask: (taskUlid: string) => void;
  /**
   * Open the event detail surface (modal in 13c). When supplied,
   * event chips become clickable; otherwise they render as static
   * placeholders.
   */
  onOpenEvent?: (eventUlid: string) => void;
  /**
   * Drag-to-reschedule for tasks. When supplied, task chips become
   * draggable and Month cells + Week all-day buckets become drop
   * targets. `dueAtDate` is a `YYYY-MM-DD` string in the user's local
   * calendar — pass it straight through to `useRescheduleTask`.
   */
  onRescheduleTask?: (taskUlid: string, dueAtDate: string) => void;
  /**
   * Drag-to-reschedule for events. Same drop targets as task drag.
   * When the destination is a Month cell or Week all-day strip, the
   * SPA computes a new `start_at` (and `end_at` for timed events,
   * preserving duration) before calling `useRescheduleCalendarEvent`.
   */
  onRescheduleEvent?: (input: {
    eventUlid: string;
    startAt: string;
    endAt: string | null;
  }) => void;
}

/* ─ Date helpers (mirror calendar.jsx) ──────────────────────────── */

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const addMonths = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
};
const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const isWeekend = (d: Date): boolean => d.getDay() === 0 || d.getDay() === 6;

const FIRST_DAY = 1; // Monday — Q6 hardcoded default
const SHOW_WEEKENDS = true; // Q6 hardcoded default
const DENSITY_VISIBLE = 3; // 'cozy' density — 3 chips before "+N more"

const fmtMonthYear = (d: Date): string =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

const fmtWeekRange = (start: Date): string => {
  const end = addDays(start, 6);
  const sM = start.toLocaleDateString(undefined, { month: 'short' });
  const eM = end.toLocaleDateString(undefined, { month: 'short' });
  if (start.getMonth() === end.getMonth()) {
    return `${sM} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${sM} ${start.getDate()} – ${eM} ${end.getDate()}, ${end.getFullYear()}`;
};

const fmtDayLong = (d: Date): string =>
  d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const startOfWeek = (d: Date, firstDay = FIRST_DAY): Date => {
  const x = startOfDay(d);
  const diff = (x.getDay() - firstDay + 7) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};

/**
 * Format a date as `YYYY-MM-DD` using local components — the same
 * calendar day the user sees in the cell, regardless of timezone.
 * Drop targets use this as their droppable id ("day:" + this) so the
 * drag handler can decode the destination date without re-parsing.
 */
const toDateKey = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Build the `start_at` + `end_at` payload for a drag-rescheduled
 * calendar event. Behaviour by event type + drop kind:
 *  - **All-day**: always day-only on the destination, regardless of
 *    whether the drop landed on a `day:` or `slot:` target. Stays
 *    all-day; user toggles via the dialog if they want to convert.
 *  - **Timed + day drop** (no `destinationHour`): preserve hour/
 *    minute of the original start_at on the destination day.
 *  - **Timed + slot drop** (`destinationHour` provided): snap the
 *    hour to the slot's hour, preserving minutes/seconds from the
 *    original. End_at shifts by the same delta as start_at so the
 *    event's duration is unchanged either way.
 *
 * `destinationDateKey` is `YYYY-MM-DD` (the local-tz date the user
 * dropped on). Returns ISO 8601 strings ready to PATCH.
 */
const computeRescheduledTimes = (
  event: { startAt: string; endAt: string | null; isAllDay: boolean },
  destinationDateKey: string,
  destinationHour?: number,
): { startAt: string; endAt: string | null } => {
  if (event.isAllDay) {
    return {
      startAt: destinationDateKey,
      endAt: event.endAt ? destinationDateKey : null,
    };
  }
  const oldStart = new Date(event.startAt);
  if (Number.isNaN(oldStart.getTime())) {
    // Defensive — shouldn't happen, but if startAt is unparseable
    // just send the destination date as start with no end (the
    // backend will reject if end_at required).
    return { startAt: destinationDateKey, endAt: null };
  }
  // Build a new local-tz Date with the (possibly slot-snapped) hour
  // on the destination day, then convert to ISO so the backend stores
  // UTC matching what the user sees.
  const [yy, mm, dd] = destinationDateKey.split('-').map(Number);
  const hour = destinationHour ?? oldStart.getHours();
  const newStart = new Date(
    yy ?? oldStart.getFullYear(),
    (mm ?? oldStart.getMonth() + 1) - 1,
    dd ?? oldStart.getDate(),
    hour,
    oldStart.getMinutes(),
    oldStart.getSeconds(),
    oldStart.getMilliseconds(),
  );
  const deltaMs = newStart.getTime() - oldStart.getTime();
  const newEnd = event.endAt
    ? new Date(new Date(event.endAt).getTime() + deltaMs)
    : null;
  return {
    startAt: newStart.toISOString(),
    endAt: newEnd ? newEnd.toISOString() : null,
  };
};

/**
 * Parse a task's `due_at` as a calendar date — local midnight on the
 * stored date, ignoring any time / tz offset on the wire.
 *
 * The API stores `due_at` in a date column and serialises it as
 * `"YYYY-MM-DD"` or `"YYYY-MM-DDT00:00:00.000000Z"`. Naïvely calling
 * `new Date(iso)` parses the second form as UTC midnight, which a
 * negative-offset timezone (e.g. America/Mexico_City UTC-6) reads
 * back as the *previous* day at 18:00 local — so a task due May 8
 * would bucket under May 7 in the calendar grid (and drop targets
 * would land tasks one day earlier than the highlighted cell).
 *
 * Pulling the date components out of the ISO string side-steps the
 * round-trip entirely: we treat `due_at` as a calendar date end-to-
 * end, which matches the server's column type and the user's mental
 * model. When timed tasks land (post-CalendarEvent model), this
 * helper is the only spot to revisit.
 *
 * Returns `null` for unparseable input (caller should skip the row).
 */
const parseCalendarDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const monthGrid = (focal: Date, firstDay = FIRST_DAY): Date[] => {
  const first = new Date(focal.getFullYear(), focal.getMonth(), 1);
  const start = startOfWeek(first, firstDay);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

const weekdayLabels = (firstDay = FIRST_DAY): string[] => {
  const sundayFirst = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const mondayFirst = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return firstDay === 0 ? sundayFirst : mondayFirst;
};

/* ─ Data adapter — bucket tasks + events by ISO date string ────── */

interface CalendarData {
  tasks: CalendarTaskRow[];
  tasksByIso: Map<string, CalendarTaskRow[]>;
  overdue: CalendarTaskRow[];
  /** All-day events bucketed by every day they span (multi-day events
   *  appear in every day's bucket from start to end inclusive). */
  allDayEventsByIso: Map<string, CalendarEventRow[]>;
  /** Timed events bucketed by their start day in local tz. Multi-day
   *  timed events render only on the start day for v1 — flagged as a
   *  v2 follow-up (rare and visually awkward in the hour grid). */
  timedEventsByIso: Map<string, CalendarEventRow[]>;
}

function useCalendarData(
  tasks: CalendarTaskRow[],
  events: ReadonlyArray<CalendarEventRow> = [],
): CalendarData {
  return useMemo(() => {
    const today = startOfDay(new Date());
    const tasksByIso = new Map<string, CalendarTaskRow[]>();
    const overdue: CalendarTaskRow[] = [];
    for (const t of tasks) {
      const due = parseCalendarDate(t.dueAt);
      if (!due) continue;
      const key = due.toDateString();
      const bucket = tasksByIso.get(key);
      if (bucket) bucket.push(t);
      else tasksByIso.set(key, [t]);
      if (!t.isComplete && due.getTime() < today.getTime()) {
        overdue.push(t);
      }
    }
    overdue.sort((a, b) => {
      const da = parseCalendarDate(a.dueAt);
      const db = parseCalendarDate(b.dueAt);
      return (da?.getTime() ?? 0) - (db?.getTime() ?? 0);
    });

    const allDayEventsByIso = new Map<string, CalendarEventRow[]>();
    const timedEventsByIso = new Map<string, CalendarEventRow[]>();
    for (const e of events) {
      if (e.isAllDay) {
        // Multi-day all-day: append to every cell from start..end. For
        // single-day all-day (`endAt = null`), just the start day.
        const start = parseCalendarDate(e.startAt);
        if (!start) continue;
        const end = e.endAt ? parseCalendarDate(e.endAt) ?? start : start;
        for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) {
          const key = d.toDateString();
          const bucket = allDayEventsByIso.get(key);
          if (bucket) bucket.push(e);
          else allDayEventsByIso.set(key, [e]);
        }
        continue;
      }
      // Timed: bucket by the local-tz start day. Use the standard
      // `new Date(iso)` here — timed events legitimately need timezone
      // interpretation (a meeting at 3pm UTC is 9am Mexico City).
      const startDate = new Date(e.startAt);
      if (Number.isNaN(startDate.getTime())) continue;
      const key = startOfDay(startDate).toDateString();
      const bucket = timedEventsByIso.get(key);
      if (bucket) bucket.push(e);
      else timedEventsByIso.set(key, [e]);
    }

    // Sort timed events within each day by start time.
    for (const list of timedEventsByIso.values()) {
      list.sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      );
    }

    return { tasks, tasksByIso, overdue, allDayEventsByIso, timedEventsByIso };
  }, [tasks, events]);
}

/* ─ Root ────────────────────────────────────────────────────────── */

type ViewMode = CalendarViewMode;

export const CalendarView = ({
  tasks,
  members,
  events = [],
  view,
  setView,
  focal,
  setFocal,
  onOpenTask,
  onOpenEvent,
  onRescheduleTask,
  onRescheduleEvent,
}: CalendarViewProps) => {
  const [filters, setFilters] = useState<{
    showTasks: boolean;
    showMeetings: boolean;
    people: string[];
  }>({ showTasks: true, showMeetings: true, people: [] });
  // ID of the currently-dragged chip, for the DragOverlay portal.
  // Prefixed: `task:{ulid}` or `event:{ulid}` so handleDragEnd can
  // route to the right reschedule callback. `null` while idle.
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Sensors mirror Board / List: small distance threshold so a single
  // click on a chip still navigates instead of arming a drag, and a
  // touch delay so phone scrolling isn't hijacked.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  // Collision detection: prefer the droppable directly under the
  // cursor (`pointerWithin`) so tiny targets like MiniMonth cells
  // (~26×32 px) and per-hour slots (~56 px tall) get reliable hits
  // even when the dragged ghost is much larger than the target rect.
  // dnd-kit's default `rectIntersection` picks "most overlap by area"
  // which biases toward larger neighbours when the ghost spans
  // multiple cells. Fall back to `closestCenter` if the pointer is
  // momentarily outside any droppable (autoscroll edges, etc.).
  const collisionDetection = useMemo<CollisionDetection>(
    () => (args) => {
      const pointerHits = pointerWithin(args);
      if (pointerHits.length > 0) return pointerHits;
      return closestCenter(args);
    },
    [],
  );

  const draggingTask = useMemo(() => {
    if (!draggingId || !draggingId.startsWith('task:')) return null;
    const id = draggingId.slice('task:'.length);
    return tasks.find((t) => t.id === id) ?? null;
  }, [draggingId, tasks]);

  const draggingEvent = useMemo(() => {
    if (!draggingId || !draggingId.startsWith('event:')) return null;
    const id = draggingId.slice('event:'.length);
    return events.find((e) => e.id === id) ?? null;
  }, [draggingId, events]);

  const handleDragStart = (event: DragStartEvent): void => {
    setDraggingId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    setDraggingId(null);
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);

    // Two drop-target id shapes:
    //   `day:YYYY-MM-DD`        — all-day-style drop (Month cells,
    //                             Week all-day strip, Day all-day
    //                             band, MiniMonth dates)
    //   `slot:YYYY-MM-DD:HH`    — timed-style drop (Week + Day hour-
    //                             grid cells; HH is 24h hour)
    let dateKey: string | null = null;
    let destinationHour: number | undefined;
    if (overId.startsWith('day:')) {
      dateKey = overId.slice('day:'.length);
    } else if (overId.startsWith('slot:')) {
      const [, key, hourStr] = overId.split(':');
      if (key) {
        dateKey = key;
        const h = Number(hourStr);
        destinationHour = Number.isFinite(h) ? h : undefined;
      }
    }
    if (!dateKey) return;
    const activeId = String(active.id);

    if (activeId.startsWith('task:')) {
      if (!onRescheduleTask) return;
      // Tasks don't have a time, so we always send the date only.
      onRescheduleTask(activeId.slice('task:'.length), dateKey);
      return;
    }

    if (activeId.startsWith('event:')) {
      if (!onRescheduleEvent) return;
      const ulid = activeId.slice('event:'.length);
      const e = events.find((x) => x.id === ulid);
      if (!e) return;
      // Day drop  → preserve time-of-day; slot drop → snap to the
      // dropped hour (preserving minutes from the original). All-day
      // events ignore hour info either way; they stay all-day.
      const result = computeRescheduledTimes(e, dateKey, destinationHour);
      onRescheduleEvent({ eventUlid: ulid, startAt: result.startAt, endAt: result.endAt });
    }
  };

  // Apply per-event-type and per-person filters before passing to views.
  const filteredTasks = useMemo(() => {
    if (!filters.showTasks) return [];
    if (filters.people.length === 0) return tasks;
    return tasks.filter(
      (t) => t.assigneeId !== null && filters.people.includes(t.assigneeId),
    );
  }, [tasks, filters]);

  // Same predicate for events — Meetings chip toggles visibility, the
  // per-person filter narrows to events where at least one attendee
  // matches. An event with NO attendees that match the people filter
  // is hidden when the filter is active.
  const filteredEvents = useMemo(() => {
    if (!filters.showMeetings) return [];
    if (filters.people.length === 0) return events;
    const allowed = new Set(filters.people);
    return events.filter((e) => e.attendees.some((a) => allowed.has(a.userId)));
  }, [events, filters]);

  // Hotkeys: D / W / M switch views; T = today; ← / → step.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === 'd') setView('day');
      else if (k === 'w') setView('week');
      else if (k === 'm') setView('month');
      else if (k === 't') setFocal(new Date());
      else if (e.key === 'ArrowLeft') {
        setFocal(
          view === 'month'
            ? addMonths(focal, -1)
            : addDays(focal, view === 'week' ? -7 : -1),
        );
      } else if (e.key === 'ArrowRight') {
        setFocal(
          view === 'month'
            ? addMonths(focal, 1)
            : addDays(focal, view === 'week' ? 7 : 1),
        );
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // setView/setFocal are stable from the page (state setters);
    // focal + view re-bind the handler so arrow keys read latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, focal]);

  const onSelectDay = (d: Date): void => {
    setFocal(d);
    if (view === 'month') setView('day');
  };

  const taskDragEnabled = Boolean(onRescheduleTask);
  const eventDragEnabled = Boolean(onRescheduleEvent);
  // dragEnabled flag is split between tasks vs events; the dragKind
  // determines which chips become drag handles. Day cells stay
  // droppable as long as EITHER kind is enabled.
  const anyDragEnabled = taskDragEnabled || eventDragEnabled;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingId(null)}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* Radial backdrop glow per `.cal-bg-glow` */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
          style={{
            background:
              'radial-gradient(60% 50% at 80% 0%, var(--brand-glow-1), transparent 70%),' +
              'radial-gradient(50% 60% at 10% 30%, var(--brand-glow-2), transparent 60%)',
          }}
        />

        {/* Shell */}
        <div className="relative z-[1] flex min-h-0 flex-1 flex-col gap-3.5 p-[22px]">
          <Header
            view={view}
            setView={setView}
            focal={focal}
            setFocal={setFocal}
            tasks={tasks}
          />
          <FilterChips filters={filters} setFilters={setFilters} members={members} />
          <OverdueLane tasks={tasks} onOpen={onOpenTask} dragEnabled={taskDragEnabled} />

          {view === 'month' ? (
            <MonthView
              focal={focal}
              tasks={filteredTasks}
              events={filteredEvents}
              onOpenTask={onOpenTask}
              onOpenEvent={onOpenEvent}
              onSelectDay={onSelectDay}
              taskDragEnabled={taskDragEnabled}
              eventDragEnabled={eventDragEnabled}
              anyDragEnabled={anyDragEnabled}
            />
          ) : null}
          {view === 'week' ? (
            <WeekView
              focal={focal}
              tasks={filteredTasks}
              events={filteredEvents}
              onOpenTask={onOpenTask}
              onOpenEvent={onOpenEvent}
              onSelectDay={onSelectDay}
              taskDragEnabled={taskDragEnabled}
              eventDragEnabled={eventDragEnabled}
              anyDragEnabled={anyDragEnabled}
            />
          ) : null}
          {view === 'day' ? (
            <DayView
              focal={focal}
              setFocal={setFocal}
              tasks={filteredTasks}
              events={filteredEvents}
              onOpenTask={onOpenTask}
              onOpenEvent={onOpenEvent}
              eventDragEnabled={eventDragEnabled}
            />
          ) : null}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {draggingTask ? <DragGhostChip task={draggingTask} /> : null}
        {draggingEvent ? <EventDragGhost event={draggingEvent} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

/* ─ Header (title + nav + view switcher) ────────────────────────── */

const Header = ({
  view,
  setView,
  focal,
  setFocal,
  tasks,
}: {
  view: ViewMode;
  setView: (v: ViewMode) => void;
  focal: Date;
  setFocal: (d: Date) => void;
  tasks: CalendarTaskRow[];
}) => {
  const data = useCalendarData(tasks);

  const titleLabel =
    view === 'month'
      ? fmtMonthYear(focal)
      : view === 'week'
        ? fmtWeekRange(startOfWeek(focal))
        : fmtDayLong(focal);

  const goPrev = (): void =>
    setFocal(
      view === 'month'
        ? addMonths(focal, -1)
        : addDays(focal, view === 'week' ? -7 : -1),
    );
  const goNext = (): void =>
    setFocal(
      view === 'month'
        ? addMonths(focal, 1)
        : addDays(focal, view === 'week' ? 7 : 1),
    );

  // Q5: subtitle shows task-due count instead of meeting/focus hours.
  const subtitle = useMemo(() => {
    if (view === 'day') {
      const iso = startOfDay(focal).toDateString();
      const t = data.tasksByIso.get(iso)?.length ?? 0;
      return t > 0 ? `${t} ${t === 1 ? 'task' : 'tasks'} due` : 'No tasks due';
    }
    if (view === 'week') {
      const start = startOfWeek(focal);
      let t = 0;
      for (let i = 0; i < 7; i++) {
        const iso = addDays(start, i).toDateString();
        t += data.tasksByIso.get(iso)?.length ?? 0;
      }
      return `${t} ${t === 1 ? 'task' : 'tasks'} due`;
    }
    return '';
  }, [view, focal, data]);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-baseline gap-4">
        <h1 className="text-foreground m-0 text-[22px] font-semibold tracking-[-0.02em] tabular-nums whitespace-nowrap">
          {titleLabel}
        </h1>
        {subtitle ? (
          <span className="text-foreground-faint text-[12.5px] tabular-nums">
            {subtitle}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center gap-1.5">
          <NavButton onClick={goPrev} ariaLabel="Previous">
            <ChevronLeft className="size-3.5" />
          </NavButton>
          <button
            type="button"
            onClick={() => setFocal(new Date())}
            className={cn(
              'text-foreground inline-flex h-7 cursor-pointer items-center rounded-lg border px-3 text-[12px] font-medium backdrop-blur-md transition-colors duration-100',
              'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
              'hover:border-foreground-faint',
            )}
          >
            Today
          </button>
          <NavButton onClick={goNext} ariaLabel="Next">
            <ChevronRight className="size-3.5" />
          </NavButton>
        </div>

        <div
          role="tablist"
          aria-label="Calendar view"
          className={cn(
            'inline-flex items-center rounded-[10px] border p-0.5',
            'border-border bg-secondary',
          )}
        >
          {(
            [
              { id: 'day', label: 'Day', kbd: 'D' },
              { id: 'week', label: 'Week', kbd: 'W' },
              { id: 'month', label: 'Month', kbd: 'M' },
            ] as const
          ).map((v) => (
            <button
              key={v.id}
              role="tab"
              aria-selected={view === v.id}
              onClick={() => setView(v.id)}
              className={cn(
                'inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-lg border-0 px-3 text-[12px] font-medium transition-colors duration-100',
                view === v.id
                  ? 'bg-background text-foreground shadow-[0_1px_2px_oklch(0_0_0/0.06)]'
                  : 'text-foreground-muted hover:text-foreground bg-transparent',
              )}
            >
              {v.label}
              <span
                className={cn(
                  'rounded-[3px] border px-1 font-mono text-[9.5px] opacity-55',
                  'border-[var(--glass-card-border)]',
                )}
              >
                {v.kbd}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const NavButton = ({
  children,
  onClick,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  ariaLabel: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    className={cn(
      'text-foreground-muted inline-flex h-7 w-7 items-center justify-center rounded-lg border backdrop-blur-md transition-colors duration-100',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
      'hover:text-foreground hover:border-foreground-faint',
    )}
  >
    {children}
  </button>
);

/* ─ FilterChips (Tasks / Meetings / per-person) ─────────────────── */

const FilterChips = ({
  filters,
  setFilters,
  members,
}: {
  filters: { showTasks: boolean; showMeetings: boolean; people: string[] };
  setFilters: (next: typeof filters) => void;
  members: CalendarMember[];
}) => {
  // Top 6 members per prototype — keeps the row narrow.
  const peopleFilters = members.slice(0, 6);
  const togglePerson = (id: string): void => {
    const set = new Set(filters.people);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setFilters({ ...filters, people: [...set] });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterChip
        active={filters.showTasks}
        onClick={() => setFilters({ ...filters, showTasks: !filters.showTasks })}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#10b981' }} />
        Tasks
      </FilterChip>
      {/* Meetings chip rendered faithfully per Q3 — filters to nothing
          today; auto-works when timed events ship. */}
      <FilterChip
        active={filters.showMeetings}
        onClick={() =>
          setFilters({ ...filters, showMeetings: !filters.showMeetings })
        }
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#3b82f6' }} />
        Meetings
      </FilterChip>
      <span
        aria-hidden="true"
        className="bg-[var(--glass-card-border)] mx-1 h-[18px] w-px"
      />
      {peopleFilters.map((p) => {
        const active = filters.people.includes(p.id);
        const firstName = (p.name ?? p.email ?? '').split(/[\s@]/)[0] ?? '';
        return (
          <FilterChip
            key={p.id}
            active={active}
            onClick={() => togglePerson(p.id)}
          >
            <MemberAvatar
              id={p.id}
              name={p.name}
              email={p.email}
              size="xs"
              className="!h-3.5 !w-3.5 !text-[7px]"
            />
            {firstName}
          </FilterChip>
        );
      })}
    </div>
  );
};

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium backdrop-blur-md transition-colors duration-100',
      active
        ? 'border-[oklch(0.74_0.16_50/0.4)] bg-[oklch(0.74_0.16_50/0.12)] text-[oklch(0.45_0.16_50)] dark:text-[oklch(0.85_0.16_50)]'
        : 'text-foreground-muted hover:text-foreground hover:border-foreground-faint border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
    )}
  >
    {children}
  </button>
);

/* ─ Overdue lane ────────────────────────────────────────────────── */

const OverdueLane = ({
  tasks,
  onOpen,
  dragEnabled = false,
}: {
  tasks: CalendarTaskRow[];
  onOpen: (taskUlid: string) => void;
  dragEnabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const data = useCalendarData(tasks);
  const today = startOfDay(new Date());

  if (data.overdue.length === 0) return null;

  return (
    <section
      className={cn(
        'overflow-hidden rounded-[14px] border backdrop-blur-md',
        'border-[oklch(0.65_0.2_25/0.25)]',
        'bg-[linear-gradient(135deg,oklch(0.65_0.2_25/0.08),oklch(0.65_0.2_25/0.03)_60%,var(--glass-card-bg))]',
        'dark:bg-[linear-gradient(135deg,oklch(0.55_0.2_25/0.18),oklch(0.55_0.2_25/0.06)_60%,var(--glass-card-bg))]',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left"
      >
        <span
          className={cn(
            'inline-flex h-[22px] w-[22px] items-center justify-center rounded-full',
            'bg-[oklch(0.65_0.2_25/0.18)] text-[oklch(0.5_0.2_25)]',
            'dark:bg-[oklch(0.55_0.2_25/0.3)] dark:text-[oklch(0.78_0.18_25)]',
          )}
        >
          <AlertTriangle className="size-3.5" />
        </span>
        <span className="text-foreground text-[13px]">
          <strong className="font-semibold tabular-nums text-[oklch(0.5_0.2_25)] dark:text-[oklch(0.78_0.18_25)]">
            {data.overdue.length}
          </strong>{' '}
          overdue {data.overdue.length === 1 ? 'task' : 'tasks'}
        </span>
        <span className="text-foreground-muted ml-auto inline-flex items-center gap-1 text-[11px]">
          {open ? 'Hide' : 'Show'}{' '}
          <span className="text-[9px]">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open ? (
        <div
          className={cn(
            'grid gap-1.5 border-t border-dashed p-1.5',
            'border-[oklch(0.65_0.2_25/0.2)]',
            'grid-cols-[repeat(auto-fill,minmax(280px,1fr))]',
          )}
        >
          {data.overdue.map((t) => {
            const due = parseCalendarDate(t.dueAt);
            const daysLate = due
              ? Math.max(
                  1,
                  Math.round((today.getTime() - due.getTime()) / 86_400_000),
                )
              : 1;
            return (
              <OverdueChip
                key={t.id}
                task={t}
                daysLate={daysLate}
                onOpen={onOpen}
                draggable={dragEnabled}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

const OverdueChip = ({
  task,
  daysLate,
  onOpen,
  draggable,
}: {
  task: CalendarTaskRow;
  daysLate: number;
  onOpen: (id: string) => void;
  draggable: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task:${task.id}`,
    disabled: !draggable,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onOpen(task.id)}
      {...(draggable ? listeners : {})}
      {...attributes}
      className={cn(
        'flex items-center gap-2 rounded-[9px] border px-2.5 py-2 text-left text-[12.5px] transition-all duration-100',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
        'hover:-translate-y-px hover:border-[var(--glass-card-hover-border)]',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        isDragging && 'opacity-30',
      )}
    >
      <span
        aria-hidden="true"
        className="h-[18px] w-[2px] flex-shrink-0 rounded-full"
        style={{ background: task.statusColor }}
      />
      <span className="text-foreground-faint flex-shrink-0 font-mono text-[10.5px]">
        {task.code}
      </span>
      <span className="text-foreground flex-1 truncate">{task.title}</span>
      <span className="flex-shrink-0 text-[10.5px] tabular-nums whitespace-nowrap text-[oklch(0.5_0.2_25)] dark:text-[oklch(0.78_0.18_25)]">
        {daysLate === 1 ? '1d late' : `${daysLate}d late`}
      </span>
    </button>
  );
};

/* ─ Surface frame ───────────────────────────────────────────────── */

const Surface = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'flex min-h-0 flex-1 flex-col overflow-hidden rounded-[16px] border backdrop-blur-[14px]',
      'border-[var(--glass-column-border)] bg-[var(--glass-column-bg)] [backdrop-filter:blur(14px)_saturate(110%)]',
      'shadow-[0_1px_2px_oklch(0.3_0.05_245/0.04),0_8px_24px_oklch(0.3_0.05_245/0.06)]',
      'dark:shadow-[0_1px_2px_black,0_12px_32px_oklch(0_0_0/0.35)]',
      className,
    )}
  >
    {children}
  </div>
);

/* ─ Event chip (all-day & month) ────────────────────────────────── */

const EventChip = ({
  task,
  onOpen,
  draggable = false,
}: {
  task: CalendarTaskRow;
  onOpen: (id: string) => void;
  /** When true, the chip joins the DndContext as a draggable. The
   *  PointerSensor's 6px activation distance keeps the click-to-open
   *  gesture intact — short clicks still navigate. */
  draggable?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task:${task.id}`,
    disabled: !draggable,
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      title={`${task.code} — ${task.title}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(task.id);
      }}
      {...(draggable ? listeners : {})}
      {...attributes}
      className={cn(
        'text-foreground relative flex w-full items-center gap-1.5 overflow-hidden rounded-[5px] border border-transparent bg-[var(--glass-card-bg)] px-1.5 py-0.5 text-[11px] leading-[1.4] backdrop-blur-[6px] transition-all duration-100',
        'hover:translate-x-px hover:border-[var(--glass-card-border)]',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        // Hide the source chip while a copy is floating in the
        // DragOverlay — avoids the "two chips" double-render and
        // makes the drop zone feedback unambiguous.
        isDragging && 'opacity-30',
      )}
    >
      <span
        aria-hidden="true"
        className="absolute top-0 bottom-0 left-0 w-[2px] rounded-r-[1px]"
        style={{ background: task.statusColor }}
      />
      <span
        className={cn(
          'flex-1 truncate pl-1.5 font-medium',
          task.isComplete && 'text-foreground-faint line-through',
        )}
      >
        {task.title}
      </span>
    </button>
  );
};

/**
 * Free-floating chip rendered inside the `DragOverlay` portal while a
 * drag is in flight. Mirrors `EventChip`'s visual but with a softer
 * shadow + slight scale so the cursor anchor reads as "this is the
 * thing you're moving" rather than blending into the grid.
 */
const DragGhostChip = ({ task }: { task: CalendarTaskRow }) => (
  <div
    className={cn(
      'text-foreground pointer-events-none relative flex max-w-[260px] items-center gap-1.5 overflow-hidden rounded-[6px] border bg-[var(--glass-card-bg)] px-1.5 py-1 text-[11px] leading-[1.4] backdrop-blur-[6px]',
      'border-[var(--glass-card-hover-border)] shadow-[0_8px_24px_oklch(0_0_0/0.18)]',
    )}
    style={{ transform: 'rotate(-1.5deg)' }}
  >
    <span
      aria-hidden="true"
      className="absolute top-0 bottom-0 left-0 w-[2px] rounded-r-[1px]"
      style={{ background: task.statusColor }}
    />
    <span
      className={cn(
        'flex-1 truncate pl-1.5 font-medium',
        task.isComplete && 'text-foreground-faint line-through',
      )}
    >
      {task.title}
    </span>
  </div>
);

const EventDragGhost = ({ event }: { event: CalendarEventRow }) => (
  <div
    className={cn(
      'text-foreground pointer-events-none relative flex max-w-[260px] items-center gap-1.5 overflow-hidden rounded-[6px] border bg-[var(--glass-card-bg)] px-1.5 py-1 text-[11px] leading-[1.4] backdrop-blur-[6px]',
      'border-[var(--brand-accent)]/60 shadow-[0_8px_24px_oklch(0_0_0/0.18)]',
    )}
    style={{ transform: 'rotate(-1.5deg)' }}
  >
    <span
      aria-hidden="true"
      className="absolute top-0 bottom-0 left-0 w-[2px] rounded-r-[1px] bg-[var(--brand-accent)]"
    />
    <span className="flex-1 truncate pl-1.5 font-medium">{event.title}</span>
  </div>
);

/* ─ Calendar event chips ────────────────────────────────────────────
 *
 * Per scoping doc Q3: events don't have a status, so we use
 * --brand-accent as a single distinguishing color (same as Today
 * highlights, the orange filter-active badge, etc.). That visually
 * separates events from tasks (which are colored by their workspace
 * status). Two variants:
 *
 *   - `EventBarChip`        — compact row, used in Month cells +
 *                             all-day strips alongside task chips
 *   - `TimedEventBlock`     — absolutely-positioned block in the
 *                             Week / Day hour grid, sized by duration
 * ─────────────────────────────────────────────────────────────── */

const EventBarChip = ({
  event,
  onOpen,
  draggable = false,
}: {
  event: CalendarEventRow;
  onOpen?: (id: string) => void;
  /** When true, joins the DndContext for drag-to-reschedule. Mirrors
   *  the task-chip pattern; the 6px activation distance keeps clicks
   *  intact. */
  draggable?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `event:${event.id}`,
    disabled: !draggable,
  });
  const clickable = Boolean(onOpen);
  return (
    <button
      ref={setNodeRef}
      type="button"
      title={event.location ? `${event.title} · ${event.location}` : event.title}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onOpen?.(event.id);
            }
          : undefined
      }
      {...(draggable ? listeners : {})}
      {...attributes}
      className={cn(
        'text-foreground relative flex w-full items-center gap-1.5 overflow-hidden rounded-[5px] border border-transparent bg-[var(--glass-card-bg)] px-1.5 py-0.5 text-[11px] leading-[1.4] backdrop-blur-[6px] transition-all duration-100',
        'hover:translate-x-px hover:border-[var(--glass-card-border)]',
        draggable ? 'cursor-grab active:cursor-grabbing' : clickable ? 'cursor-pointer' : 'cursor-default',
        isDragging && 'opacity-30',
      )}
    >
      <span
        aria-hidden="true"
        className="absolute top-0 bottom-0 left-0 w-[2px] rounded-r-[1px] bg-[var(--brand-accent)]"
      />
      <span className="flex-1 truncate pl-1.5 font-medium">{event.title}</span>
      {event.attendees.length > 1 ? (
        <span
          aria-label={`${event.attendees.length} attendees`}
          className="text-foreground-faint flex-shrink-0 text-[10px] tabular-nums"
        >
          {event.attendees.length}
        </span>
      ) : null}
    </button>
  );
};

/**
 * Format a timed-event start as `2:30 PM` for chip labels.
 */
const fmtEventTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Compute the {top, height} px geometry of a timed event inside the
 * hour grid. Events outside the visible range (HOURS = 7..19) get
 * clamped to the edge so they're at least visible — clipped events
 * are flagged for v2 with a small indicator (not in v1).
 */
const eventGridGeometry = (
  startAt: string,
  endAt: string | null,
): { top: number; height: number } | null => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return null;
  // Default to 60 minutes when the event has no end_at (defensive —
  // timed events should always have end_at per Q4, but the form
  // request only enforces it on create).
  const end = endAt
    ? new Date(endAt)
    : new Date(start.getTime() + 60 * 60_000);
  if (Number.isNaN(end.getTime())) return null;

  const startMins = start.getHours() * 60 + start.getMinutes();
  const endMins = Math.max(end.getHours() * 60 + end.getMinutes(), startMins + 15);
  const gridStart = (HOURS[0] ?? 0) * 60;
  const gridEnd = ((HOURS[HOURS.length - 1] ?? 19) + 1) * 60;

  const clampedStart = Math.max(startMins, gridStart);
  const clampedEnd = Math.min(endMins, gridEnd);
  if (clampedEnd <= gridStart || clampedStart >= gridEnd) return null;

  const top = ((clampedStart - gridStart) / 60) * HOUR_HEIGHT;
  const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT, 18);
  return { top, height };
};

/**
 * Hour-grid block for timed events. Absolutely positioned; the
 * containing column must be `relative`. Uses brand-accent left bar
 * and a translucent brand-accent fill so the block reads as "on
 * top" of the empty hour grid. Click-to-open via `onOpen`; drag-to-
 * reschedule when `draggable` is true (drops onto Month / Week
 * all-day cells; the source's hour-grid context isn't a drop target
 * per Q11).
 */
const TimedEventBlock = ({
  event,
  onOpen,
  draggable = false,
}: {
  event: CalendarEventRow;
  onOpen?: (id: string) => void;
  draggable?: boolean;
}) => {
  const geo = eventGridGeometry(event.startAt, event.endAt);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `event:${event.id}`,
    disabled: !draggable,
  });
  if (!geo) return null;
  const clickable = Boolean(onOpen);
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onOpen?.(event.id);
            }
          : undefined
      }
      {...(draggable ? listeners : {})}
      {...attributes}
      title={event.location ? `${event.title} · ${event.location}` : event.title}
      className={cn(
        'absolute left-1 right-1 z-[2] flex flex-col items-start gap-0.5 overflow-hidden rounded-[6px] border px-1.5 py-1 text-left text-[11px] leading-[1.25] backdrop-blur-[6px] transition-all duration-100',
        'border-[var(--brand-accent)]/40 bg-[oklch(0.74_0.16_50/0.12)] text-foreground',
        'dark:bg-[oklch(0.74_0.16_50/0.18)]',
        draggable
          ? 'cursor-grab active:cursor-grabbing hover:bg-[oklch(0.74_0.16_50/0.20)] dark:hover:bg-[oklch(0.74_0.16_50/0.28)]'
          : clickable
            ? 'cursor-pointer hover:bg-[oklch(0.74_0.16_50/0.20)] dark:hover:bg-[oklch(0.74_0.16_50/0.28)]'
            : 'cursor-default',
        isDragging && 'opacity-30',
      )}
      style={{ top: geo.top, height: geo.height }}
    >
      <span className="w-full truncate font-semibold">{event.title}</span>
      <span className="text-foreground-faint truncate text-[10px] tabular-nums">
        {fmtEventTime(event.startAt)}
        {event.endAt ? ` – ${fmtEventTime(event.endAt)}` : ''}
      </span>
    </button>
  );
};

/* ─ Month view ──────────────────────────────────────────────────── */

const MonthView = ({
  focal,
  tasks,
  events,
  onOpenTask,
  onOpenEvent,
  onSelectDay,
  taskDragEnabled = false,
  eventDragEnabled = false,
  anyDragEnabled = false,
}: {
  focal: Date;
  tasks: CalendarTaskRow[];
  events: ReadonlyArray<CalendarEventRow>;
  onOpenTask: (id: string) => void;
  onOpenEvent?: (id: string) => void;
  onSelectDay: (d: Date) => void;
  taskDragEnabled?: boolean;
  eventDragEnabled?: boolean;
  anyDragEnabled?: boolean;
}) => {
  const days = useMemo(() => monthGrid(focal), [focal]);
  const labels = useMemo(() => weekdayLabels(), []);
  const data = useCalendarData(tasks, events);
  const today = startOfDay(new Date());

  // SHOW_WEEKENDS = true; render all 7 columns
  const cols = 7;

  return (
    <Surface>
      <div
        className={cn(
          'grid flex-shrink-0 border-b',
          'border-[var(--glass-card-border)] [background:linear-gradient(180deg,var(--glass-card-bg),transparent)]',
        )}
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {labels.map((l) => (
          <div
            key={l}
            className="text-foreground-faint px-3 py-2.5 text-left text-[10.5px] font-semibold tracking-[0.06em] uppercase"
          >
            {l}
          </div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridAutoRows: 'minmax(0, 1fr)',
        }}
      >
        {days.map((d, i) => {
          const iso = d.toDateString();
          const dayTasks = data.tasksByIso.get(iso) ?? [];
          const dayEvents = [
            ...(data.allDayEventsByIso.get(iso) ?? []),
            ...(data.timedEventsByIso.get(iso) ?? []),
          ];
          // Events render first (above task chips), then tasks fill
          // the remaining density budget. Hidden count rolls up the
          // overflow from BOTH lists so the "+N more" is honest.
          const combinedCount = dayTasks.length + dayEvents.length;
          const visibleEvents = dayEvents.slice(0, DENSITY_VISIBLE);
          const remainingBudget = Math.max(
            0,
            DENSITY_VISIBLE - visibleEvents.length,
          );
          const visibleTasks = dayTasks.slice(0, remainingBudget);
          const hidden =
            combinedCount - visibleEvents.length - visibleTasks.length;
          return (
            <MonthDayCell
              key={iso}
              date={d}
              focal={focal}
              today={today}
              visibleTasks={visibleTasks}
              visibleEvents={visibleEvents}
              hiddenCount={hidden}
              indexInGrid={i}
              onSelectDay={onSelectDay}
              onOpenTask={onOpenTask}
              onOpenEvent={onOpenEvent}
              taskDragEnabled={taskDragEnabled}
              eventDragEnabled={eventDragEnabled}
              droppable={anyDragEnabled}
            />
          );
        })}
      </div>
    </Surface>
  );
};

const MonthDayCell = ({
  date,
  focal,
  today,
  visibleTasks,
  visibleEvents,
  hiddenCount,
  indexInGrid,
  onSelectDay,
  onOpenTask,
  onOpenEvent,
  taskDragEnabled,
  eventDragEnabled,
  droppable,
}: {
  date: Date;
  focal: Date;
  today: Date;
  visibleTasks: CalendarTaskRow[];
  visibleEvents: CalendarEventRow[];
  hiddenCount: number;
  indexInGrid: number;
  onSelectDay: (d: Date) => void;
  onOpenTask: (id: string) => void;
  onOpenEvent?: (id: string) => void;
  taskDragEnabled: boolean;
  eventDragEnabled: boolean;
  droppable: boolean;
}) => {
  const isOther = date.getMonth() !== focal.getMonth();
  const col = indexInGrid % 7;
  const row = Math.floor(indexInGrid / 7);
  const dateKey = toDateKey(date);
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${dateKey}`,
    disabled: !droppable,
  });

  return (
    <div
      ref={setNodeRef}
      onClick={() => onSelectDay(date)}
      className={cn(
        'relative flex min-h-0 cursor-pointer flex-col gap-0.5 overflow-hidden p-1.5 px-2 pb-2',
        'border-l border-t border-[var(--glass-card-border)] transition-[background] duration-100',
        col === 0 && 'border-l-0',
        row === 0 && 'border-t-0',
        isOther && 'bg-[oklch(0_0_0/0.015)] dark:bg-[oklch(0_0_0/0.18)]',
        isWeekend(date) && !isOther && 'bg-[oklch(0_0_0/0.01)] dark:bg-[oklch(0_0_0/0.1)]',
        'hover:bg-[oklch(0.74_0.16_50/0.04)] dark:hover:bg-[oklch(0.74_0.16_50/0.06)]',
        // Drop-target highlight — brand-accent tinted background +
        // inset ring when a draggable hovers over this cell.
        isOver &&
          'bg-[oklch(0.74_0.16_50/0.12)] dark:bg-[oklch(0.74_0.16_50/0.18)] ring-1 ring-inset ring-[var(--brand-accent)]',
      )}
    >
      <span
        className={cn(
          'inline-flex h-[22px] min-w-[22px] items-center justify-center self-start rounded-full px-1.5 text-[12px] font-medium tabular-nums',
          isOther && 'text-foreground-faint opacity-60',
          !isOther && 'text-foreground',
          sameDay(date, today) &&
            'bg-[var(--brand-accent)] font-semibold text-white shadow-[0_0_0_4px_oklch(0.74_0.16_50/0.15)]',
        )}
      >
        {date.getDate()}
      </span>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5">
        {visibleEvents.map((e) => (
          <EventBarChip
            key={e.id}
            event={e}
            onOpen={onOpenEvent}
            draggable={eventDragEnabled}
          />
        ))}
        {visibleTasks.map((t) => (
          <EventChip key={t.id} task={t} onOpen={onOpenTask} draggable={taskDragEnabled} />
        ))}
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectDay(date);
            }}
            className={cn(
              'text-foreground-muted hover:text-foreground self-start rounded border-0 bg-transparent px-1.5 py-px text-[10.5px] font-medium',
              'hover:bg-[oklch(0_0_0/0.04)] dark:hover:bg-[oklch(1_0_0/0.06)]',
            )}
          >
            +{hiddenCount} more
          </button>
        ) : null}
      </div>
    </div>
  );
};

/* ─ Hour grid utilities ─────────────────────────────────────────── */

const HOUR_HEIGHT = 56;
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am – 7pm

const fmtHourLabel = (h: number): string => {
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${h < 12 ? 'AM' : 'PM'}`;
};

/* Now-line — orange 2px + dot, only shown when current time falls
   inside the visible hour range. Updates every 60s. */
const NowLine = ({ visible = true }: { visible?: boolean }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  const startMins = (HOURS[0] ?? 0) * 60;
  const endMins = ((HOURS[HOURS.length - 1] ?? 19) + 1) * 60;
  if (!visible || mins < startMins || mins > endMins) return null;
  const top = ((mins - startMins) / 60) * HOUR_HEIGHT;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute right-0 left-0 z-[3] h-0 border-t-2 border-[var(--brand-accent)]"
      style={{ top }}
    >
      <span
        className={cn(
          'absolute -top-[5px] -left-[5px] h-2 w-2 rounded-full bg-[var(--brand-accent)]',
          'shadow-[0_0_0_3px_oklch(0.74_0.16_50/0.25)]',
        )}
      />
    </div>
  );
};

/* ─ Week view ───────────────────────────────────────────────────── */

const WeekView = ({
  focal,
  tasks,
  events,
  onOpenTask,
  onOpenEvent,
  onSelectDay,
  taskDragEnabled = false,
  eventDragEnabled = false,
  anyDragEnabled = false,
}: {
  focal: Date;
  tasks: CalendarTaskRow[];
  events: ReadonlyArray<CalendarEventRow>;
  onOpenTask: (id: string) => void;
  onOpenEvent?: (id: string) => void;
  onSelectDay: (d: Date) => void;
  taskDragEnabled?: boolean;
  eventDragEnabled?: boolean;
  anyDragEnabled?: boolean;
}) => {
  const start = useMemo(() => startOfWeek(focal), [focal]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(start, i)),
    [start],
  );
  const data = useCalendarData(tasks, events);
  const today = startOfDay(new Date());

  const colCount = days.length;
  const gridTemplate = `64px repeat(${colCount}, 1fr)`;

  return (
    <Surface className="flex flex-col">
      {/* All-day strip */}
      <div
        className={cn(
          'grid flex-shrink-0 border-b border-[var(--glass-card-border)]',
          '[background:linear-gradient(180deg,var(--glass-card-bg),transparent)]',
        )}
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div className="text-foreground-faint flex items-start px-2.5 py-2 text-[9.5px] font-medium tracking-[0.04em] capitalize">
          All-day
        </div>
        {days.map((d) => {
          const iso = d.toDateString();
          const t = data.tasksByIso.get(iso) ?? [];
          const e = data.allDayEventsByIso.get(iso) ?? [];
          return (
            <WeekAllDayCell
              key={iso}
              date={d}
              tasks={t}
              events={e}
              onOpenTask={onOpenTask}
              onOpenEvent={onOpenEvent}
              taskDragEnabled={taskDragEnabled}
              eventDragEnabled={eventDragEnabled}
              droppable={anyDragEnabled}
            />
          );
        })}
      </div>

      {/* Day headers */}
      <div
        className="grid flex-shrink-0 border-b border-[var(--glass-card-border)]"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div />
        {days.map((d) => {
          const isToday = sameDay(d, today);
          return (
            <button
              key={d.toDateString()}
              type="button"
              onClick={() => onSelectDay(d)}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center border-0 bg-transparent px-1 py-2.5 transition-colors duration-100',
                'hover:bg-[oklch(0.74_0.16_50/0.05)]',
              )}
            >
              <span
                className={cn(
                  'text-foreground-faint text-[10.5px] font-medium tracking-[0.04em] capitalize',
                  isToday && 'text-[oklch(0.55_0.18_50)]',
                )}
              >
                {d.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span
                className={cn(
                  'text-foreground mt-1 inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-[16px] font-medium tabular-nums',
                  isToday &&
                    'bg-[var(--brand-accent)] text-white shadow-[0_0_0_4px_oklch(0.74_0.16_50/0.15)]',
                )}
              >
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Hour grid */}
      <div
        className="relative grid min-h-0 flex-1 overflow-y-auto"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div
          className={cn(
            'sticky left-0 z-[2] flex flex-col border-r border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
          )}
        >
          {HOURS.map((h) => (
            <div
              key={h}
              className="text-foreground-faint relative flex items-start justify-end px-2 pt-1 font-mono text-[9.5px] tabular-nums"
              style={{ height: HOUR_HEIGHT }}
            >
              {fmtHourLabel(h)}
            </div>
          ))}
        </div>
        {days.map((d, dayIdx) => {
          const isToday = sameDay(d, today);
          const iso = d.toDateString();
          const dayTimed = data.timedEventsByIso.get(iso) ?? [];
          return (
            <WeekHourGridColumn
              key={d.toDateString()}
              date={d}
              isToday={isToday}
              dayIdx={dayIdx}
              dayTimed={dayTimed}
              onSelectDay={onSelectDay}
              onOpenEvent={onOpenEvent}
              eventDragEnabled={eventDragEnabled}
              droppable={anyDragEnabled}
            />
          );
        })}
      </div>
    </Surface>
  );
};

const WeekAllDayCell = ({
  date,
  tasks,
  events,
  onOpenTask,
  onOpenEvent,
  taskDragEnabled,
  eventDragEnabled,
  droppable,
}: {
  date: Date;
  tasks: CalendarTaskRow[];
  events: CalendarEventRow[];
  onOpenTask: (id: string) => void;
  onOpenEvent?: (id: string) => void;
  taskDragEnabled: boolean;
  eventDragEnabled: boolean;
  droppable: boolean;
}) => {
  const dateKey = toDateKey(date);
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${dateKey}`,
    disabled: !droppable,
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex max-h-[76px] min-h-[32px] flex-col gap-0.5 overflow-y-auto p-1.5 transition-[background] duration-100',
        isOver &&
          'bg-[oklch(0.74_0.16_50/0.12)] dark:bg-[oklch(0.74_0.16_50/0.18)] ring-1 ring-inset ring-[var(--brand-accent)]',
      )}
    >
      {events.map((e) => (
        <EventBarChip
          key={e.id}
          event={e}
          onOpen={onOpenEvent}
          draggable={eventDragEnabled}
        />
      ))}
      {tasks.map((task) => (
        <EventChip
          key={task.id}
          task={task}
          onOpen={onOpenTask}
          draggable={taskDragEnabled}
        />
      ))}
    </div>
  );
};

/**
 * Week view's hour-grid day column. Now uses 13 per-hour droppables
 * stacked vertically (one per visible hour, 7am–7pm). Drop on hour H
 * = reschedule to that day at H:00 preserving minutes from the
 * original event. The whole column is also click-to-navigate via
 * `onSelectDay` — clicks bubble up from the inert hour cells.
 *
 * Per-hour granularity matches the visual hour rows the user sees;
 * 30-minute slots would feel unnecessarily fiddly given the dashed
 * half-hour line is a guide, not a snap point.
 */
const WeekHourGridColumn = ({
  date,
  isToday,
  dayIdx,
  dayTimed,
  onSelectDay,
  onOpenEvent,
  eventDragEnabled,
  droppable,
}: {
  date: Date;
  isToday: boolean;
  dayIdx: number;
  dayTimed: CalendarEventRow[];
  onSelectDay: (d: Date) => void;
  onOpenEvent?: (id: string) => void;
  eventDragEnabled: boolean;
  droppable: boolean;
}) => {
  const dateKey = toDateKey(date);

  return (
    <div
      onClick={() => onSelectDay(date)}
      className={cn(
        'relative cursor-pointer border-l border-[var(--glass-card-border)]',
        dayIdx === 0 && 'border-l-0',
        isWeekend(date) && 'bg-[oklch(0_0_0/0.012)] dark:bg-[oklch(0_0_0/0.12)]',
        isToday && 'bg-[oklch(0.74_0.16_50/0.04)] dark:bg-[oklch(0.74_0.16_50/0.07)]',
      )}
    >
      {HOURS.map((h, i) => (
        <HourSlotDroppable
          key={h}
          dateKey={dateKey}
          hour={h}
          isFirst={i === 0}
          isLast={i === HOURS.length - 1}
          droppable={droppable}
        />
      ))}
      {dayTimed.map((e) => (
        <TimedEventBlock
          key={e.id}
          event={e}
          onOpen={onOpenEvent}
          draggable={eventDragEnabled}
        />
      ))}
      {isToday ? <NowLine /> : null}
    </div>
  );
};

/**
 * One hour-row inside a Week / Day hour grid. A drop target with id
 * `slot:{dateKey}:{hour}`; the parent column reads its rendered DOM
 * order to compute geometry for `<TimedEventBlock>` which sits on
 * top of these cells (absolutely positioned).
 */
const HourSlotDroppable = ({
  dateKey,
  hour,
  isFirst,
  isLast,
  droppable,
}: {
  dateKey: string;
  hour: number;
  isFirst: boolean;
  isLast: boolean;
  droppable: boolean;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot:${dateKey}:${hour}`,
    disabled: !droppable,
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'border-t border-[var(--glass-card-border)] transition-[background] duration-100',
        isFirst && 'border-t-0',
        // Half-hour dashed line guide (visual only — not a separate
        // drop target; per-30-min granularity isn't worth the UX cost).
        !isLast &&
          "after:mt-[28px] after:block after:h-[28px] after:border-t after:border-dashed after:border-[oklch(0_0_0/0.04)] dark:after:border-[oklch(1_0_0/0.04)]",
        isOver &&
          'bg-[oklch(0.74_0.16_50/0.18)] ring-1 ring-inset ring-[var(--brand-accent)]',
      )}
      style={{ height: HOUR_HEIGHT }}
    />
  );
};

/* ─ Day view ────────────────────────────────────────────────────── */

const DayView = ({
  focal,
  setFocal,
  tasks,
  events,
  onOpenTask,
  onOpenEvent,
  eventDragEnabled = false,
}: {
  focal: Date;
  setFocal: (d: Date) => void;
  tasks: CalendarTaskRow[];
  events: ReadonlyArray<CalendarEventRow>;
  onOpenTask: (id: string) => void;
  onOpenEvent?: (id: string) => void;
  /** When true, event chips become draggable. Drop targets in Day
   *  view: the per-hour cells in the hour grid (changes time-of-day
   *  on the same day) + the MiniMonth dates in the right rail
   *  (changes the day, preserving time). */
  eventDragEnabled?: boolean;
}) => {
  const focalDateKey = toDateKey(focal);
  const data = useCalendarData(tasks, events);
  const iso = startOfDay(focal).toDateString();
  const today = startOfDay(new Date());
  const isToday = sameDay(focal, today);

  const dueTasks = data.tasksByIso.get(iso) ?? [];
  const allDayEvents = data.allDayEventsByIso.get(iso) ?? [];
  const timedEvents = data.timedEventsByIso.get(iso) ?? [];
  const dayLabel = focal.toLocaleDateString(undefined, { weekday: 'long' });
  const monthLabel = focal.toLocaleDateString(undefined, { month: 'long' });

  // Distinct attendees across every event (all-day + timed) on the
  // focal day — powers the "Today's people" rail. Map by user id so
  // the same person across multiple meetings only appears once;
  // sort alphabetically so the order is stable across re-renders.
  const todaysPeople = useMemo(() => {
    const seen = new Map<string, { userId: string; name: string | null; email: string | null }>();
    for (const e of [...allDayEvents, ...timedEvents]) {
      for (const a of e.attendees) {
        if (!seen.has(a.userId)) seen.set(a.userId, a);
      }
    }
    return [...seen.values()].sort((a, b) => {
      const an = (a.name ?? a.email ?? '').toLowerCase();
      const bn = (b.name ?? b.email ?? '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, [allDayEvents, timedEvents]);

  const totalEventCount = allDayEvents.length + timedEvents.length;

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 gap-3.5',
        'grid-cols-[1fr_320px]',
        '[@media(max-width:1100px)]:grid-cols-[1fr_280px]',
        '[@media(max-width:900px)]:grid-cols-[1fr]',
      )}
    >
      {/* Main agenda */}
      <Surface className="flex flex-col">
        <div
          className={cn(
            'flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b px-[18px] py-3',
            'border-[var(--glass-card-border)] [background:linear-gradient(180deg,var(--glass-card-bg),transparent)]',
          )}
        >
          <div className="flex min-w-0 items-baseline gap-3">
            <span className="text-foreground-faint text-[11px] font-semibold tracking-[0.08em] uppercase">
              {dayLabel}
            </span>
            <span
              className={cn(
                'text-foreground text-[28px] leading-none font-semibold tracking-[-0.02em] tabular-nums',
                isToday && 'text-[var(--brand-accent)]',
              )}
            >
              {focal.getDate()}
            </span>
            <span className="text-foreground-faint text-[11px] font-semibold tracking-[0.08em] uppercase opacity-60">
              {monthLabel}
            </span>
          </div>
          {/* Header stats — task count + event count, side by side. */}
          <div className="text-foreground-muted flex flex-wrap items-center gap-3.5 text-[11.5px]">
            <span className="whitespace-nowrap">
              <strong className="text-foreground mr-0.5 font-semibold tabular-nums">
                {dueTasks.length}
              </strong>
              {dueTasks.length === 1 ? 'task due' : 'tasks due'}
            </span>
            {totalEventCount > 0 ? (
              <span className="whitespace-nowrap">
                <strong className="text-foreground mr-0.5 font-semibold tabular-nums">
                  {totalEventCount}
                </strong>
                {totalEventCount === 1 ? 'event' : 'events'}
              </span>
            ) : null}
          </div>
        </div>

        {/* All-day band — task chips + event chips. Renders when
            EITHER list has rows so an all-day event still anchors a
            visible band even when no tasks are due that day. */}
        {dueTasks.length > 0 || allDayEvents.length > 0 ? (
          <div
            className={cn(
              'flex flex-shrink-0 flex-wrap items-center gap-2.5 border-b px-3.5 py-2',
              'border-[var(--glass-card-border)]',
            )}
          >
            <span className="text-foreground-faint flex-shrink-0 text-[9.5px] font-semibold tracking-[0.06em] uppercase">
              All-day · {dueTasks.length + allDayEvents.length}
            </span>
            {allDayEvents.map((e) => (
              <span key={e.id} className="inline-flex max-w-xs">
                <EventBarChip
                  event={e}
                  onOpen={onOpenEvent}
                  draggable={eventDragEnabled}
                />
              </span>
            ))}
            {dueTasks.map((t) => (
              <span key={t.id} className="inline-flex max-w-xs">
                <EventChip task={t} onOpen={onOpenTask} />
              </span>
            ))}
          </div>
        ) : null}

        {/* Hour grid */}
        <div
          className="relative grid min-h-0 flex-1 overflow-y-auto"
          style={{ gridTemplateColumns: '64px 1fr' }}
        >
          <div
            className={cn(
              'sticky left-0 z-[2] flex flex-col border-r border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
            )}
          >
            {HOURS.map((h) => (
              <div
                key={h}
                className="text-foreground-faint relative flex items-start justify-end px-2 pt-1 font-mono text-[9.5px] tabular-nums"
                style={{ height: HOUR_HEIGHT }}
              >
                {fmtHourLabel(h)}
              </div>
            ))}
          </div>
          <div
            className={cn(
              'relative',
              isWeekend(focal) && 'bg-[oklch(0_0_0/0.012)] dark:bg-[oklch(0_0_0/0.12)]',
              isToday && 'bg-[oklch(0.74_0.16_50/0.04)] dark:bg-[oklch(0.74_0.16_50/0.07)]',
            )}
          >
            {HOURS.map((h, i) => (
              <HourSlotDroppable
                key={h}
                dateKey={focalDateKey}
                hour={h}
                isFirst={i === 0}
                isLast={i === HOURS.length - 1}
                droppable={eventDragEnabled}
              />
            ))}
            {timedEvents.map((e) => (
              <TimedEventBlock
                key={e.id}
                event={e}
                onOpen={onOpenEvent}
                draggable={eventDragEnabled}
              />
            ))}
            {isToday ? <NowLine /> : null}
          </div>
        </div>
      </Surface>

      {/* Right rail */}
      <aside className="flex min-h-0 flex-col gap-3.5">
        <MiniMonth
          focal={focal}
          setFocal={setFocal}
          tasks={tasks}
          events={events}
          droppable={eventDragEnabled}
        />
        <RailCard title="Tasks due" pill={dueTasks.length}>
          {dueTasks.length === 0 ? (
            <div className="text-foreground-faint px-3 py-4 text-center text-[11.5px]">
              Nothing due today.
            </div>
          ) : (
            dueTasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onOpenTask(t.id)}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-2.5 py-1.5 text-left transition-[background] duration-100',
                  'hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
                  t.isComplete && '[&>span:nth-child(2)>div:nth-child(2)]:line-through [&>span:nth-child(2)>div:nth-child(2)]:text-foreground-faint',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-6 w-[2px] flex-shrink-0 rounded-full"
                  style={{ background: t.statusColor }}
                />
                <span className="min-w-0 flex-1">
                  <div className="text-foreground-faint font-mono text-[10px]">
                    {t.code}
                  </div>
                  <div className="text-foreground mt-px truncate text-[12px]">
                    {t.title}
                  </div>
                </span>
                {t.assigneeId ? (
                  <MemberAvatar
                    id={t.assigneeId}
                    name={t.assigneeName}
                    email={t.assigneeEmail}
                    size="xs"
                    className="!h-5 !w-5 !text-[8px]"
                  />
                ) : null}
              </button>
            ))
          )}
        </RailCard>
        {/* Slice 13b — distinct attendees from today's events
            (all-day + timed). Sort alphabetical for stability. Empty
            state stays the same when there's nothing on the day's
            calendar. */}
        <RailCard title="Today's people" pill={todaysPeople.length || undefined}>
          {todaysPeople.length === 0 ? (
            <div className="text-foreground-faint px-3 py-4 text-center text-[11.5px]">
              No meetings today.
            </div>
          ) : (
            todaysPeople.map((p) => (
              <div
                key={p.userId}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
              >
                <MemberAvatar
                  id={p.userId}
                  name={p.name}
                  email={p.email}
                  size="xs"
                  className="!h-5 !w-5 !text-[8px]"
                />
                <span className="text-foreground min-w-0 flex-1 truncate text-[12px]">
                  {p.name ?? p.email ?? 'Unknown'}
                </span>
              </div>
            ))
          )}
        </RailCard>
      </aside>
    </div>
  );
};

const RailCard = ({
  title,
  pill,
  children,
}: {
  title: string;
  pill?: number;
  children: ReactNode;
}) => (
  <div
    className={cn(
      'flex flex-col overflow-hidden rounded-[14px] border backdrop-blur-[14px]',
      'border-[var(--glass-column-border)] bg-[var(--glass-column-bg)] [backdrop-filter:blur(14px)_saturate(110%)]',
    )}
  >
    <div className="flex items-center justify-between border-b border-[var(--glass-card-border)] px-3.5 pt-3 pb-2">
      <span className="text-foreground-muted text-[11px] font-semibold tracking-[0.06em] uppercase">
        {title}
      </span>
      {typeof pill === 'number' ? (
        <span
          className={cn(
            'text-foreground-muted rounded-full px-[7px] py-px text-[10px] font-semibold tabular-nums',
            'bg-[var(--surface-quiet)]',
          )}
        >
          {pill}
        </span>
      ) : null}
    </div>
    <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">{children}</div>
  </div>
);

const MiniMonth = ({
  focal,
  setFocal,
  tasks,
  events = [],
  droppable = false,
}: {
  focal: Date;
  setFocal: (d: Date) => void;
  tasks: CalendarTaskRow[];
  /** Events on the focal month — feeds the per-cell dot indicator
   *  alongside tasks so a day with an event but no task still shows
   *  a marker. Optional; older callers pass tasks-only. */
  events?: ReadonlyArray<CalendarEventRow>;
  /** When true, every date cell becomes a drop target (id `day:YYYY-MM-DD`).
   *  Day view passes this so users can drag a chip from the agenda
   *  onto a different day in the rail — Day view's only useful drop
   *  target since the single-day layout has no in-grid destinations. */
  droppable?: boolean;
}) => {
  const [viewMonth, setViewMonth] = useState<Date>(
    () => new Date(focal.getFullYear(), focal.getMonth(), 1),
  );
  useEffect(() => {
    setViewMonth(new Date(focal.getFullYear(), focal.getMonth(), 1));
  }, [focal]);
  const days = useMemo(() => monthGrid(viewMonth), [viewMonth]);
  const data = useCalendarData(tasks, events);
  const today = startOfDay(new Date());
  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-[14px] border backdrop-blur-[14px]',
        'border-[var(--glass-column-border)] bg-[var(--glass-column-bg)] [backdrop-filter:blur(14px)_saturate(110%)]',
      )}
    >
      <div className="px-3.5 pt-3 pb-3.5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-foreground text-[12px] font-semibold tabular-nums">
            {fmtMonthYear(viewMonth)}
          </span>
          <div className="inline-flex gap-0.5">
            <button
              type="button"
              onClick={() => setViewMonth((v) => addMonths(v, -1))}
              className={cn(
                'text-foreground-muted hover:text-foreground inline-flex h-[22px] w-[22px] items-center justify-center rounded border-0 bg-transparent',
                'hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
              )}
              aria-label="Previous month"
            >
              <ChevronLeft className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => setViewMonth((v) => addMonths(v, 1))}
              className={cn(
                'text-foreground-muted hover:text-foreground inline-flex h-[22px] w-[22px] items-center justify-center rounded border-0 bg-transparent',
                'hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
              )}
              aria-label="Next month"
            >
              <ChevronRight className="size-3" />
            </button>
          </div>
        </div>
        <div
          className="grid gap-px"
          style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}
        >
          {labels.map((l, i) => (
            <span
              key={i}
              className="text-foreground-faint inline-flex h-[22px] items-center justify-center text-[10.5px] font-semibold tabular-nums"
            >
              {l}
            </span>
          ))}
          {days.map((d) => {
            const iso = d.toDateString();
            const isOther = d.getMonth() !== viewMonth.getMonth();
            const isTodayDay = sameDay(d, today);
            const isSelected = sameDay(d, focal);
            // Dot fires when the day has anything at all on it —
            // tasks, all-day events, or timed events.
            const hasMarker =
              (data.tasksByIso.get(iso)?.length ?? 0) > 0 ||
              (data.allDayEventsByIso.get(iso)?.length ?? 0) > 0 ||
              (data.timedEventsByIso.get(iso)?.length ?? 0) > 0;
            return (
              <MiniMonthCell
                key={iso}
                date={d}
                isOther={isOther}
                isTodayDay={isTodayDay}
                isSelected={isSelected}
                hasEvents={hasMarker}
                droppable={droppable}
                onSelect={setFocal}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MiniMonthCell = ({
  date,
  isOther,
  isTodayDay,
  isSelected,
  hasEvents,
  droppable,
  onSelect,
}: {
  date: Date;
  isOther: boolean;
  isTodayDay: boolean;
  isSelected: boolean;
  hasEvents: boolean;
  droppable: boolean;
  onSelect: (d: Date) => void;
}) => {
  const dateKey = toDateKey(date);
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${dateKey}`,
    disabled: !droppable,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onSelect(date)}
      className={cn(
        'text-foreground-muted relative inline-flex h-[26px] cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-[10.5px] tabular-nums',
        'hover:text-foreground hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
        isOther && 'text-foreground-faint opacity-50',
        isTodayDay && !isSelected && 'text-[var(--brand-accent)] font-semibold',
        isSelected &&
          !isTodayDay &&
          'text-foreground border-foreground-faint border bg-[var(--glass-card-bg)] font-semibold',
        isSelected &&
          isTodayDay &&
          'border-[var(--brand-accent)] bg-[var(--brand-accent)] !text-white',
        hasEvents &&
          "after:absolute after:mt-4 after:h-[3px] after:w-[3px] after:rounded-full after:bg-[var(--brand-accent)] after:content-['']",
        // Drop-target highlight while a draggable hovers — same
        // brand-accent treatment as Month / Week droppables.
        isOver && 'bg-[oklch(0.74_0.16_50/0.20)] ring-1 ring-inset ring-[var(--brand-accent)]',
      )}
    >
      {date.getDate()}
    </button>
  );
};

/* ─ TaskRow type re-export — also used by the page-side adapter
 *   for consistency with TaskList's TaskRow. ─────────────────── */

export type { CalendarTaskRow as TaskRow };
